/**
 * ProtectionPanel — the seven questions an operator opens an entity to answer.
 *
 * 🛑 WHY THIS COMPONENT EXISTS.
 *
 * The backup section is twelve tabs with ZERO cross-links between them, and the entity pages
 * fetched almost nothing: `LakehouseDetailPage` loaded the entity and its run history;
 * `WarehouseDetailPage` loaded only the entity, so no page anywhere showed a warehouse's backup
 * state. Six of the seven questions below had answers sitting in the database with no route to
 * the screen — and an answer you cannot reach is indistinguishable from one that does not exist.
 *
 *   is it protected at all?          which plan governs it
 *   when does it next run?           next armed schedule
 *   what can I restore it to?        recovery points
 *   would that restore work?         last verification verdict
 *   how long is it kept?             the retention the ENFORCER applies, and at which level
 *   where does it physically live?   storage account + region
 *   did it run?                      last run outcome
 *
 * Every value comes from one call to `/backup/protection/:type/:id`, which composes the canonical
 * resolvers rather than re-querying, so this panel cannot disagree with what actually happens.
 *
 * Design: tokens only (Principle XLV), lucide-react icons, no hand-rolled cards.
 */
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck, ShieldAlert, ShieldOff, Clock, Archive, BadgeCheck,
  HardDrive, History, PauseCircle, Ban,
} from 'lucide-react';
import { Card, Badge, Spinner } from '@/components/ui';
// The canonical client (origin base, identity injected). 🛑 Not utils/apiClient: its base is VITE_API_BASE_URL || '/api',
// so with this /api-prefixed path the prod build called /api/api/v1/... and got 401 on every detail page (screen
// audit, prod pass 2026-10-28, F12); the dev rig hid it because there the variable is the backend origin.
import { apiClient } from '@/services/apiClient';
import { formatEastern } from '@/utils/dateFormatter';

export interface ProtectionPanelProps {
  entityType: 'lakehouse' | 'warehouse';
  entityId: string;
}

interface ScheduleSummary {
  backupType: string;
  cronExpression: string;
  isActive: boolean;
  isEnabled: boolean;
  nextRunAt: string | null;
}

interface Protection {
  displayName: string | null;
  protectionTier: string | null;
  exclusion: { reasonCategory: string; note: string | null; excludedAt: string | null } | null;
  plan: { id: string; name: string; status: string; reason: string } | null;
  schedules: ScheduleSummary[];
  nextRunAt: string | null;
  suspendedButEnabled: boolean;
  recoveryPoints: { count: number; newest: string | null; oldest: string | null };
  verification: {
    verdict: string | null; method: string | null; verifiedAt: string | null;
    coherentTables: number | null; totalTables: number | null;
  } | null;
  retention: { summary: string; source: string; graceDays: number | null } | null;
  storage: { accountName: string | null; displayName: string | null; region: string | null } | null;
  lastRun: { status: string; startedAt: string; tables: number | null; bytes: number | null } | null;
}

const fmtBytes = (n: number | null): string => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};

const when = (iso: string | null): string => (iso ? formatEastern(iso) : '—');

/** One labelled answer. Kept deliberately uniform so the panel scans as a checklist. */
function Answer({
  icon, label, children, tone = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad';
}) {
  const toneClass =
    tone === 'good' ? 'text-[var(--color-status-success)]'
    : tone === 'warn' ? 'text-[var(--color-status-warning)]'
    : tone === 'bad' ? 'text-[var(--color-status-danger)]'
    : 'text-[var(--color-text-secondary)]';
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className={`mt-0.5 shrink-0 ${toneClass}`}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">{label}</div>
        <div className="mt-0.5 text-sm text-[var(--color-text-primary)]">{children}</div>
      </div>
    </div>
  );
}

export function ProtectionPanel({ entityType, entityId }: ProtectionPanelProps) {
  const { data, isLoading, error } = useQuery<Protection>({
    queryKey: ['entity-protection', entityType, entityId],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Protection }>(
        `/api/v1/backup/protection/${entityType}/${entityId}`
      );
      return res.data.data;
    },
    enabled: !!entityId,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex min-h-[120px] items-center justify-center"><Spinner size="md" /></div>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="p-6 text-sm text-[var(--color-text-secondary)]">
        Protection status could not be loaded for this entity.
      </Card>
    );
  }

  const armed = data.schedules.filter((s) => s.isActive);

  // 🛑 "Cannot" outranks "is not". An `unsupported` exclusion means the platform has no path to
  //    back this up at all — a warehouse with no SQL endpoint has no export to run — which is a
  //    different fact from an entity that simply has no schedule, and demands a different action
  //    (provision the endpoint, or accept it). `operator`/`decommissioned` are choices someone
  //    made. Rendering all three as "Not protected" would let a capability gap hide behind a
  //    policy decision, which is how rxmanager-wh sat excluded-yet-armed for a month.
  const cannotBackUp = data.exclusion?.reasonCategory === 'unsupported';
  // 🛑 The headline is deliberately the STRICTEST true statement, not the friendliest one.
  //    An entity on a plan with every schedule suspended is NOT protected, and a green tick
  //    there would be the same false reassurance this whole audit was about.
  const isProtected = !!data.plan && armed.length > 0 && !data.exclusion;
  const headline = cannotBackUp
    ? { icon: <Ban size={18} />, text: 'Cannot be backed up', tone: 'bad' as const }
    : data.exclusion
      ? { icon: <ShieldOff size={18} />, text: `Excluded from backup (${data.exclusion.reasonCategory})`, tone: 'warn' as const }
      : isProtected
        ? { icon: <ShieldCheck size={18} />, text: 'Protected', tone: 'good' as const }
        : data.suspendedButEnabled
          ? { icon: <PauseCircle size={18} />, text: 'Paused — not turned back on', tone: 'bad' as const }
          : data.plan
            ? { icon: <ShieldAlert size={18} />, text: 'On a plan, but nothing is scheduled', tone: 'warn' as const }
            : { icon: <ShieldOff size={18} />, text: 'Not protected', tone: 'bad' as const };

  const verdictTone =
    data.verification?.verdict === 'recoverable' ? 'good'
    : data.verification?.verdict === 'degraded' ? 'warn'
    : data.verification?.verdict ? 'bad'
    : 'neutral';

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3 border-b border-[var(--color-border)] pb-3">
        <div className="flex items-center gap-2">
          <span className={
            headline.tone === 'good' ? 'text-[var(--color-status-success)]'
            : headline.tone === 'warn' ? 'text-[var(--color-status-warning)]'
            : 'text-[var(--color-status-danger)]'
          }>{headline.icon}</span>
          <span className="text-base font-semibold text-[var(--color-text-primary)]">{headline.text}</span>
        </div>
        {data.protectionTier && <Badge variant="info" isOutlined>{data.protectionTier}</Badge>}
      </div>

      {/* The reason sits ABOVE the seven answers, because when it applies the rest are moot:
          "no armed schedule" is not the actionable fact when the platform has no way to run one. */}
      {data.exclusion && (
        <div className={`mb-3 rounded-[var(--radius-md)] border p-3 text-sm ${
          cannotBackUp
            ? 'border-[var(--color-status-danger)] bg-[var(--color-status-danger-subtle,transparent)]'
            : 'border-[var(--color-border)]'
        }`}>
          <div className="font-medium text-[var(--color-text-primary)]">
            {cannotBackUp
              ? 'This entity has no backup path'
              : `Deliberately excluded — ${data.exclusion.reasonCategory}`}
          </div>
          {data.exclusion.note && (
            <div className="mt-1 text-xs text-[var(--color-text-secondary)]">{data.exclusion.note}</div>
          )}
          <div className="mt-1 text-xs text-[var(--color-text-secondary)]">
            Recorded {when(data.exclusion.excludedAt)}
          </div>
        </div>
      )}

      <div className="divide-y divide-[var(--color-border)]">
        <Answer icon={<ShieldCheck size={16} />} label="Backup plan">
          {data.plan
            ? <>{data.plan.name} <span className="text-[var(--color-text-secondary)]">({data.plan.status})</span></>
            : <span className="text-[var(--color-text-secondary)]">No plan governs this entity</span>}
        </Answer>

        <Answer
          icon={<Clock size={16} />}
          label="Next run"
          tone={data.suspendedButEnabled ? 'bad' : 'neutral'}
        >
          {armed.length === 0 ? (
            data.suspendedButEnabled ? (
              <>
                <span className="text-[var(--color-status-danger)]">
                  {data.schedules.length} schedule(s) suspended while still marked enabled
                </span>
                <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                  Paused and never resumed — re-arm it, or clear the enabled flag if this entity is
                  genuinely no longer protected.
                </div>
              </>
            ) : (
              <span className="text-[var(--color-text-secondary)]">No armed schedule</span>
            )
          ) : (
            <>
              {when(data.nextRunAt)}
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                {armed.map((s) => `${s.backupType} (${s.cronExpression})`).join(' · ')}
              </div>
            </>
          )}
        </Answer>

        <Answer icon={<Archive size={16} />} label="Restore points">
          {data.recoveryPoints.count > 0 ? (
            <>
              {data.recoveryPoints.count.toLocaleString()} available
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                newest {when(data.recoveryPoints.newest)} · oldest {when(data.recoveryPoints.oldest)}
              </div>
            </>
          ) : (
            <span className="text-[var(--color-status-danger)]">None — nothing to restore from</span>
          )}
        </Answer>

        <Answer icon={<BadgeCheck size={16} />} label="Would a restore work?" tone={verdictTone}>
          {data.verification?.verdict ? (
            <>
              {data.verification.verdict}
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                {data.verification.coherentTables !== null && data.verification.totalTables !== null
                  ? `${data.verification.coherentTables}/${data.verification.totalTables} tables coherent · `
                  : ''}
                {data.verification.method ?? 'unknown method'} · {when(data.verification.verifiedAt)}
              </div>
              {/* Honest about what the cheap check does and does not prove. */}
              {data.verification.method === 'metadata' && (
                <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                  Metadata verification proves the backup is internally coherent, not that a restore
                  has been performed.
                </div>
              )}
            </>
          ) : (
            <span className="text-[var(--color-text-secondary)]">Never verified</span>
          )}
        </Answer>

        <Answer icon={<History size={16} />} label="Retention">
          {data.retention ? (
            <>
              {data.retention.summary}
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                set at the <span className="font-medium">{data.retention.source}</span> level
                {data.retention.graceDays !== null ? ` · ${data.retention.graceDays}d grace` : ''}
                {/* Source beats plan; an operator editing the plan on a source-overridden entity
                    changes nothing, and that has to be visible here. */}
                {data.retention.source === 'source' ? ' — an entity override, so plan edits will not change it' : ''}
              </div>
            </>
          ) : (
            <span className="text-[var(--color-text-secondary)]">No retention resolves</span>
          )}
        </Answer>

        <Answer icon={<HardDrive size={16} />} label="Stored in">
          {data.storage?.accountName ? (
            <>
              {data.storage.accountName}
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                {data.storage.displayName ?? ''}{data.storage.region ? ` · ${data.storage.region}` : ''}
              </div>
            </>
          ) : (
            <span className="text-[var(--color-status-danger)]">No active storage binding</span>
          )}
        </Answer>

        <Answer
          icon={<History size={16} />}
          label="Last run"
          tone={data.lastRun?.status === 'Completed' ? 'good' : data.lastRun ? 'warn' : 'neutral'}
        >
          {data.lastRun ? (
            <>
              {data.lastRun.status} · {when(data.lastRun.startedAt)}
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                {/* Spec 298 FR-006 (row 10): the run's table count is nullable end to end. */}
                {data.lastRun.tables != null ? `${data.lastRun.tables} table(s)` : 'tables not recorded'} · {fmtBytes(data.lastRun.bytes)}
              </div>
            </>
          ) : (
            <span className="text-[var(--color-text-secondary)]">Never run</span>
          )}
        </Answer>
      </div>
    </Card>
  );
}

export default ProtectionPanel;
